import React, { useState, useEffect } from 'react';
import TopAddress from '../components/TopAddress';
import { callGetWebservice } from '../hooks/api';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import StaticPageHeader from '../components/common/StaticPageHeader';
import '../assets/css/allpages.css';

const OrderSkeleton = () => {
  return (
    <div className="row">
      {[1, 2, 3].map((item) => (
        <div key={item} className="col-12 mb-3">
          <div className="bg-white" style={{ borderRadius: '16px', border: '1px solid #f3f4f6', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
            <div className="p-4 border-bottom bg-light d-flex justify-content-between">
              <div className="skeleton-bar w-25" style={{ height: '20px' }}></div>
              <div className="skeleton-bar w-15" style={{ height: '20px' }}></div>
            </div>
            <div className="p-4">
              <div className="skeleton-bar w-40 mb-4" style={{ height: '24px' }}></div>
              <div className="d-flex gap-3">
                <div className="skeleton-bar rounded-circle" style={{ width: '64px', height: '64px' }}></div>
                <div className="skeleton-bar rounded-circle" style={{ width: '64px', height: '64px' }}></div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const MyOrders = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [orders, setOrders] = useState({
    data: [],
    currentPage: 1,
    lastPage: 1,
    total: 0,
    from: 0,
    to: 0,
    loading: true
  });

  const fetchOrders = async (pageNo = 1) => {
    setOrders(prev => ({ ...prev, loading: true }));
    callGetWebservice(`orders?page=${pageNo}`, {}, (response) => {
      const responseData = response.data;
      setOrders(prev => ({
        ...prev,
        data: pageNo === 1 ? responseData.data : [...prev.data, ...responseData.data],
        currentPage: responseData.current_page,
        lastPage: responseData.last_page,
        from: responseData.from,
        to: responseData.to,
        total: responseData.total,
        loading: false
      }));
    }, null, dispatch, navigate);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const loadMoreOrders = () => {
    fetchOrders(orders.currentPage + 1);
  };

  const getStatusStyle = (status) => {
    if (!status) return { bg: '#e0f2fe', text: '#0369a1', dot: '#0ea5e9' }; // info
    const s = status.toString().toLowerCase();
    if (s.includes('pending') || s.includes('new')) return { bg: '#fff7ed', text: '#c2410c', dot: '#f97316' }; // warning/orange
    if (s.includes('processing')) return { bg: '#e0f2fe', text: '#0369a1', dot: '#0ea5e9' }; // blue
    if (s.includes('complete') || s.includes('delivered')) return { bg: '#dcfce7', text: '#15803d', dot: '#22c55e' }; // green
    if (s.includes('cancel')) return { bg: '#fee2e2', text: '#b91c1c', dot: '#ef4444' }; // red
    if (s.includes('ship')) return { bg: '#fef3c7', text: '#b45309', dot: '#f59e0b' }; // yellow
    return { bg: '#f3f4f6', text: '#374151', dot: '#9ca3af' }; // gray
  };

  return (
    <div className="static-page-wrapper min-vh-100 pb-5" style={{ backgroundColor: '#f9fafb' }}>
      <StaticPageHeader title="Order History" />

      <div className="container static-page-container mt-4">
        <div className="row align-items-start mt-1">
          <TopAddress />
          <div className="col-12 col-md-8">
            <div className="bg-white border-0 p-3 p-md-4 mb-3" style={{ borderRadius: '20px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.01)' }}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h3 className="fw-bold text-dark m-0" style={{ fontSize: '1.85rem', letterSpacing: '-0.5px' }}>Order History</h3>
                <span className="badge bg-light text-dark px-3 py-2" style={{ borderRadius: '20px', fontSize: '0.85rem' }}>{orders.total} Orders</span>
              </div>
              <p className="text-secondary mb-3" style={{ fontSize: '1.05rem' }}>Track and manage your recent orders seamlessly.</p>
              
              <div className="row align-items-stretch">
                  {orders.loading && orders.data.length === 0 ? (
                    <OrderSkeleton />
                  ) : orders.data.length > 0 ? (
                    orders.data.map((order, index) => {
                      const statusStyle = getStatusStyle(order?.order_status?.name || order?.order_status_id);
                      return (
                      <div key={index} className="col-12 mb-3">
                        <div className="bg-white transition-hover position-relative" style={{ borderRadius: '16px', border: '1px solid #f3f4f6', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)', overflow: 'hidden', transition: 'transform 0.2s ease, box-shadow 0.2s ease' }}
                             onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.05)'; }}
                             onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -2px rgba(0, 0, 0, 0.05)'; }}
                        >
                          
                          {/* Card Header */}
                          <div className="p-4 d-flex flex-column flex-md-row justify-content-between align-items-md-start" style={{ backgroundColor: '#fcfcfd', borderBottom: '1px solid #f3f4f6' }}>
                            <div className="d-flex flex-wrap" style={{ gap: '1.5rem' }}>
                              <div>
                                <div className="text-muted fw-semibold text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.7rem' }}>Order Placed</div>
                                <div className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>{order?.created_at}</div>
                              </div>
                              <div>
                                <div className="text-muted fw-semibold text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.7rem' }}>Total Amount</div>
                                <div className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>{order?.currency?.symbol} {order.total_amount}</div>
                              </div>
                              <div>
                                <div className="text-muted fw-semibold text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.7rem' }}>Order ID</div>
                                <div className="fw-bold text-dark" style={{ fontSize: '0.95rem' }}>#{order?.slug}</div>
                              </div>
                              <div>
                                <div className="text-muted fw-semibold text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.7rem' }}>Payment Status</div>
                                <div className="fw-bold text-success" style={{ fontSize: '0.95rem' }}>{order?.payment_status || 'Paid'}</div>
                              </div>
                              <div>
                                <div className="text-muted fw-semibold text-uppercase mb-1" style={{ letterSpacing: '1px', fontSize: '0.7rem' }}>Order Status</div>
                                <div className="mt-1">
                                  <span style={{ 
                                    display: 'inline-flex', alignItems: 'center', padding: '4px 10px', borderRadius: '20px', 
                                    backgroundColor: statusStyle.bg, color: statusStyle.text, fontSize: '0.75rem', fontWeight: '700' 
                                  }}>
                                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: statusStyle.dot, marginRight: '6px' }}></span>
                                    {(order?.order_status?.name || order?.order_status_id || 'Processing').toString().toUpperCase()}
                                  </span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="mt-3 mt-md-0">
                               <Link to={`/orderdetail/${order?.slug}`} className="btn btn-outline-dark fw-bold py-2 px-4 text-dark" style={{ borderRadius: '10px', fontSize: '0.85rem', borderWidth: '1.5px' }}>
                                 View Details
                               </Link>
                            </div>
                          </div>
                          
                          {/* Card Body */}
                          {order?.order_items && order.order_items.length > 0 && (
                            <div className="p-4">
                              <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                                <div className="mb-2 mb-md-0 w-100">
                                  <div className="d-flex flex-wrap" style={{ gap: '1rem' }}>
                                     {order.order_items.slice(0, 5).map((item, i) => (
                                        <div key={i} className="rounded-circle bg-white d-flex justify-content-center align-items-center" style={{ width: '70px', height: '70px', border: '1px solid #e5e7eb', boxShadow: '0 2px 4px rgba(0,0,0,0.02)', padding: '5px', overflow: 'hidden' }}>
                                           <img src={item?.itemable?.image || 'https://via.placeholder.com/70'} alt="Product" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
                                        </div>
                                     ))}
                                     {order.order_items.length > 5 && (
                                        <div className="rounded-circle d-flex align-items-center justify-content-center text-dark fw-bold" style={{ width: '70px', height: '70px', border: '2px dashed #d1d5db', backgroundColor: '#f9fafb', fontSize: '0.95rem' }}>
                                           +{order.order_items.length - 5}
                                        </div>
                                     )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )})
                  ) : (
                    <div className="col-12">
                      <div className="p-5 text-center bg-white" style={{ borderRadius: '20px', border: '2px dashed #e5e7eb', backgroundColor: '#fafafb' }}>
                        <div className="d-inline-flex align-items-center justify-content-center mb-4" style={{ width: '100px', height: '100px', borderRadius: '50%', backgroundColor: '#f3f4f6' }}>
                            <i className="fas fa-box-open text-muted" style={{ fontSize: '2.5rem' }}></i>
                        </div>
                        <h4 className="fw-bold text-dark mb-3">No Orders Found</h4>
                        <p className="text-secondary mb-5" style={{ fontSize: '1.05rem', maxWidth: '400px', margin: '0 auto' }}>You haven't placed any orders yet. Discover our amazing products and place your first order!</p>
                        <Link to="/productlist" className="btn btn-dark px-5 py-3 fw-bold shadow" style={{ borderRadius: '12px', fontSize: '1.05rem' }}>Start Shopping Now</Link>
                      </div>
                    </div>
                  )}
              </div>

              {/* Load More Button */}
              {orders.currentPage < orders.lastPage && (
                <div className="row mt-5">
                  <div className="col-12 text-center">
                    <button
                      onClick={loadMoreOrders}
                      disabled={orders.loading}
                      className="btn px-5 py-3 fw-bold transition-hover"
                      style={{ borderRadius: '12px', backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb' }}
                    >
                      {orders.loading ? (
                        <><i className="fas fa-spinner fa-spin mr-2"></i>Loading...</>
                      ) : 'Load More Orders'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyOrders;